import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSettings, digits } from "@/context/SettingsContext";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import {
  Flame, Sparkles, Crown, ShieldCheck, Truck, Gift, ArrowRight, MessageCircle, Landmark,
} from "lucide-react";

/*
  Deepavali 2026 – Premium Villakus & Deepams landing page.
  Route: /deepavali-2026
  - Cinematic hero (countdown will be re-enabled ~1–2 weeks before Deepavali)
  - Featured Peacock Villaku hero product
  - Auto-curated "Lamps & Deepams" grid (products whose name/slug/tags match lamp keywords)
  - Muhurat + gifting sections
  - Bulk-order WhatsApp CTA
*/

const LAMP_MATCH = (p) => {
  const bag = [
    p.name || "",
    p.slug || "",
    (p.tags || []).join(" "),
    p.category || "",
    p.subcategory || "",
  ].join(" ").toLowerCase();
  return ["deepam", "villaku", "diya", "lamp", "deepak", "peacock"].some((k) => bag.includes(k));
};

export default function Deepavali() {
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    (async () => {
      const [feat, all] = await Promise.all([
        api.get("/products/peacock-villaku-german-silver-heavy-pair").catch(() => null),
        api.get("/products", { params: { limit: 100 } }),
      ]);
      if (feat?.data?.product) setFeatured(feat.data.product);
      const lamps = (all.data.items || []).filter(LAMP_MATCH).sort((a, b) => (a.price || 0) - (b.price || 0));
      setProducts(lamps);
    })();
  }, []);

  const rest = useMemo(
    () => products.filter((p) => p.slug !== "peacock-villaku-german-silver-heavy-pair"),
    [products]
  );

  const { settings } = useSettings();
  const waNumber = digits(settings.whatsapp_number);
  const waMsg = encodeURIComponent(
    "Hello Adiyogi Handicrafts, I'm looking for premium Villakus / Deepams for Deepavali 2026. Please share available designs, sizes and pricing."
  );
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : null;

  const seoSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Deepavali 2026 · Premium Villakus & Deepams — Adiyogi Handicrafts",
    description:
      "Shop premium German Silver Peacock Villakus, Brass Diyas and traditional Deepams for Deepavali 2026 from Adiyogi Handicrafts. Government-recognised handicrafts exporter.",
  };

  return (
    <div data-testid="deepavali-page">
      <SEO
        title="Deepavali 2026 · Premium Villakus & Deepams | Adiyogi Handicrafts"
        description="Curated Peacock Villakus, German Silver Deepams & Brass Diyas for Deepavali 2026. Sold as pair or singles. All-India shipping · Prepaid UPI. A Karnataka Government initiative · EPCH awarded."
        schema={seoSchema}
      />

      {/* 1 · CINEMATIC HERO */}
      <section className="relative overflow-hidden bg-black text-ivory" data-testid="deepavali-hero">
        <div
          className="absolute inset-0 bg-center bg-cover kenburns-3"
          style={{
            backgroundImage:
              "url(https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/73blf55p_IMG_20260819_224404%5B1%5D.jpg)",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/25" />
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 240px rgba(184,134,11,0.35)" }} />
        {/* Floating golden particles */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {Array.from({ length: 30 }).map((_, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-[#E8C878]/80 animate-drift"
              style={{
                width: `${2 + (i % 4)}px`,
                height: `${2 + (i % 4)}px`,
                left: `${(i * 37) % 100}%`,
                top: `${(i * 23) % 100}%`,
                animationDelay: `${(i * 0.35) % 9}s`,
                animationDuration: `${9 + (i % 8)}s`,
                filter: "blur(1px)",
              }}
            />
          ))}
        </div>

        <div className="relative container-x py-24 md:py-32 min-h-[560px] flex items-center">
          <div className="max-w-2xl fade-in-slow">
            <div className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.45em] text-[#E8C878] mb-5">
              <Flame className="w-3.5 h-3.5" strokeWidth={1.5} />
              Deepavali 2026 · Festive Edit
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl leading-[1.02] tracking-tight">
              Light up your <span className="gold-text italic">Deepavali</span>
              <br />
              with timeless craftsmanship.
            </h1>
            <p className="mt-6 text-sm md:text-lg text-ivory/85 max-w-xl">
              Handcrafted Peacock Villakus, German Silver Deepams and traditional Brass Diyas — sold as pairs & singles,
              made to be lit for generations.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#featured-lamps" className="btn-gold rounded-full px-7 py-3 text-xs uppercase tracking-widest" data-testid="hero-shop-deepams">
                Shop the Deepavali Edit
              </a>
              {waLink && <a href={waLink} target="_blank" rel="noreferrer noopener" className="rounded-full px-7 py-3 text-xs uppercase tracking-widest border border-[#E8C878]/60 text-ivory hover:bg-white/10 transition inline-flex items-center gap-2" data-testid="hero-bulk-cta">
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Order on WhatsApp
              </a>}
            </div>
          </div>
        </div>
      </section>

      {/* 2 · TRUST RIBBON */}
      <section className="bg-espresso text-ivory">
        <div className="container-x py-3 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#E8C878]">
            <Landmark className="w-3.5 h-3.5" strokeWidth={1.5} />
            A Karnataka Government Initiative
          </div>
          <span className="hidden md:inline text-ivory/40">·</span>
          <div className="text-[11px] md:text-sm text-ivory/85">
            EPCH awarded · Government of India · Ministry of Textiles
          </div>
        </div>
      </section>

      {/* 3 · FEATURED PEACOCK VILLAKU */}
      {featured && (
        <section className="container-x py-16 md:py-24" data-testid="featured-peacock">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-muted shadow-xl">
              <img
                src={featured.images?.[0]}
                alt={featured.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-espresso/85 backdrop-blur-sm border border-[#E8C878]/40 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-[#E8C878]">
                <Crown className="w-3 h-3" strokeWidth={1.5} /> Editor&apos;s Pick
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.45em] gold-text mb-3">The Hero of the Festival</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-4">
                Premium Antique <span className="gold-text italic">Peacock Villaku</span>
                <span className="block">Heavy Weight · Sold as a Pair</span>
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-5 max-w-lg">
                Hand-detailed peacock crowns, temple-grade base engravings and an Antique German Silver finish —
                a Deepavali centrepiece that stands tall in every mandap. Six sizes (12&quot;–24&quot;) and four
                designs; the price you see is <span className="text-espresso font-medium">per Pair</span>.
              </p>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Starting at</span>
                <span className="font-serif text-4xl gold-text">₹{featured.price}</span>
                <span className="text-sm text-espresso font-medium">per Pair</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/product/${featured.slug}`}
                  className="btn-gold rounded-full px-7 py-2.5 text-xs uppercase tracking-widest"
                  data-testid="featured-shop-cta"
                >
                  View Sizes &amp; Designs <ArrowRight className="inline w-3.5 h-3.5 ml-1" strokeWidth={1.5} />
                </Link>
                {waLink && <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-outline-gold rounded-full px-7 py-2.5 text-xs uppercase tracking-widest"
                  data-testid="featured-wa-cta"
                >
                  Enquire on WhatsApp
                </a>}
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} /> All-India shipping</span>
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} /> Prepaid UPI · No login needed</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 4 · LAMPS & DEEPAMS GRID */}
      <section className="bg-muted/40 border-y hairline" id="featured-lamps" data-testid="deepams-grid">
        <div className="container-x py-16 md:py-24">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-[0.4em] gold-text mb-2">The Deepavali Edit</div>
            <h2 className="font-serif text-3xl md:text-5xl">Lamps &amp; Deepams for every mandap</h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3 max-w-xl mx-auto">
              A curated selection of Peacock Villakus, German Silver Deepams and Brass Diyas — from daily aarti to
              temple-grade centrepieces.
            </p>
          </div>
          {rest.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="deepams-list">
              {rest.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-10 text-sm">Loading the Deepavali edit…</div>
          )}
          <div className="text-center mt-10">
            <Link
              to="/shop?category=pooja-items"
              className="btn-outline-gold rounded-full px-8 py-3 text-xs uppercase tracking-widest inline-flex items-center gap-2"
              data-testid="shop-all-pooja-cta"
            >
              Explore All Pooja Items <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* 5 · DEEPAVALI STORY / MUHURAT */}
      <section className="container-x py-16 md:py-24" data-testid="deepavali-story">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {[
            {
              icon: Flame,
              title: "Light Over Darkness",
              body: "Deepavali marks the triumph of light — every lit deepam is a quiet vow to keep that light burning inside our homes.",
            },
            {
              icon: Sparkles,
              title: "Lakshmi Pooja Muhurat",
              body: "Adorn your mandap with a paired villaku, a kalash and a Varalakshmi face — the traditional arrangement inviting prosperity home.",
            },
            {
              icon: Gift,
              title: "The Gift That Lasts",
              body: "Handcrafted lamps outlive the festival — an heirloom-quality gift for weddings, housewarmings and new beginnings.",
            },
          ].map((s) => (
            <div key={s.title} className="border hairline rounded-xl p-7 bg-white hover:shadow-sm transition-shadow">
              <div className="w-10 h-10 rounded-full bg-[#B8860B]/10 flex items-center justify-center gold-text mb-4">
                <s.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="font-serif text-xl leading-tight mb-2">{s.title}</div>
              <div className="text-sm text-muted-foreground">{s.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 6 · BULK ORDER CTA */}
      <section className="relative overflow-hidden bg-espresso text-ivory">
        <div
          className="absolute inset-0 opacity-30 bg-center bg-cover"
          style={{ backgroundImage: "url(/adiyogi-hero.jpg)" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/60" />
        <div className="relative container-x py-16 md:py-20 grid md:grid-cols-[1fr_auto] items-center gap-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.45em] text-[#E8C878] mb-3">
              Corporate &amp; Bulk Gifting
            </div>
            <h3 className="font-serif text-3xl md:text-5xl leading-tight">
              Gifting Deepavali? <span className="gold-text italic">Let&apos;s make it memorable.</span>
            </h3>
            <p className="text-sm md:text-base text-ivory/80 max-w-2xl mt-4">
              Curate a bespoke set of villakus, diyas and pooja essentials for your team, clients or wedding
              guests. Our team handles size, engraving and delivery — you approve the mockups.
            </p>
          </div>
          {waLink && <a
            href={waLink}
            target="_blank"
            rel="noreferrer noopener"
            className="btn-gold rounded-full px-8 py-3.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 whitespace-nowrap"
            data-testid="bulk-wa-cta"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp for Bulk Enquiry
          </a>}
        </div>
      </section>

      {/* 7 · SPIRITUAL QUOTE */}
      <section className="relative bg-ivory border-y hairline overflow-hidden" data-testid="deepavali-quote">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #B8860B 0px, transparent 40%), radial-gradient(circle at 80% 70%, #B8860B 0px, transparent 45%)",
          }}
        />
        <div className="relative container-x py-16 md:py-24 text-center">
          <div className="text-[10px] uppercase tracking-[0.5em] gold-text mb-5">A Reflection</div>
          <blockquote className="font-serif text-2xl md:text-4xl leading-snug text-espresso max-w-3xl mx-auto italic">
            “May every lit lamp this Deepavali carry the <span className="gold-text not-italic">warmth of your intentions</span>.”
          </blockquote>
          <div className="mt-8 flex justify-center">
            <span className="inline-block w-16 h-px bg-[#B8860B]/50" />
          </div>
        </div>
      </section>
    </div>
  );
}
